/**
 * BalanceHistoryChart Component
 * Time-series line chart showing balance over time
 * WP-FLOW-11: Balance history chart visualization
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { accountsService, type BalanceHistoryPeriod } from '@/services/accounts';
import { formatCurrency, formatDate } from '@/utils/format';
import Spinner from '@/components/ui/Spinner';
import Alert from '@/components/ui/Alert';
import { cn } from '@/utils/cn';

const PERIODS: { value: BalanceHistoryPeriod; label: string }[] = [
  { value: '1Y', label: '1 Year' },
  { value: '3Y', label: '3 Years' },
  { value: '5Y', label: '5 Years' },
  { value: 'ALL', label: 'All Time' },
];

export interface BalanceHistoryChartProps {
  accountId: string;
  currency?: string;
  className?: string;
}

/**
 * Transformed chart data point for Recharts
 */
interface ChartDataPoint {
  date: string;
  balance: number;
  isAnchored: boolean;
  confidence: string;
}

export default function BalanceHistoryChart({
  accountId,
  currency = 'USD',
  className,
}: BalanceHistoryChartProps) {
  const [period, setPeriod] = useState<BalanceHistoryPeriod>('1Y');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['balance-history-chart', accountId, period],
    queryFn: () => accountsService.getBalanceHistoryTimeSeries(accountId, period),
  });

  // Transform API response to chart data format
  // Uses generated types - field names: asOfDate, balance, isAnchored, confidence
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data?.points) return [];
    return data.points.map((point) => ({
      date: point.asOfDate,
      balance: Number(point.balance),
      isAnchored: point.isAnchored,
      confidence: point.confidence,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className={cn('flex justify-center items-center h-64', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="error" className={className}>
        Failed to load balance history chart.{' '}
        <button type="button" className="underline" onClick={() => refetch()}>
          Try again
        </button>
      </Alert>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className={cn('text-center py-12 text-gray-500', className)}>
        <p>No balance history available for this period</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Period Selector */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-lg border border-gray-200 p-1">
          {PERIODS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setPeriod(value)}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                period === value
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              )}
              aria-pressed={period === value}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="date"
              tickFormatter={(date: string) => formatChartDate(date, period)}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tickFormatter={(value: number) => formatCompactCurrency(value)}
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              width={80}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const point = payload[0].payload as ChartDataPoint;
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                    <p className="text-sm text-gray-500">{formatDate(point.date)}</p>
                    <p className="text-lg font-semibold">{formatCurrency(point.balance, currency)}</p>
                    <p className="text-xs text-gray-400">
                      {point.isAnchored ? 'Verified balance' : 'Derived balance'}
                    </p>
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="balance"
              stroke="#2563EB"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props as { cx: number; cy: number; payload: ChartDataPoint };
                if (payload.isAnchored) {
                  return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={4} fill="#2563EB" stroke="#fff" strokeWidth={2} />;
                }
                return <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={3} fill="#fff" stroke="#2563EB" strokeWidth={2} />;
              }}
              activeDot={{ r: 6, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-blue-600" />
          <span>Verified balance</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full border-2 border-blue-600 bg-white" />
          <span>Derived balance</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Format date for chart X-axis labels based on time period
 * Uses shorter format for 1 year, month+year for longer periods
 */
function formatChartDate(dateStr: string, period: BalanceHistoryPeriod): string {
  const date = new Date(dateStr);
  if (period === '1Y') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

/**
 * Format currency in compact form for Y-axis labels
 * Keeps values readable without taking too much space
 */
function formatCompactCurrency(value: number): string {
  if (Math.abs(value) >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (Math.abs(value) >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}
